import {
  SORT_ASCENDING,
  SORT_DESCENDING,
} from '@/components/TaskList/JobListDef'
import {
  CompanyConsumption,
  InvoiceInfo,
  JobDetail,
  Kpi,
  ServiceInfo,
  TimeStampValue,
  UserInfo,
} from '@amplify3/duplo-back-end'
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit'

export interface Webhook {
  company_id: string
  id: string
  name: string
  url: string
}

interface WebhookLog {
  id: string
  webhook_id: string
  job_id: string
  company_id: string
  service_name: string
  status: string
  message: string
  date: string
  duration_call: number
}

export interface Country {
  code: string
  name: string
  isEU: boolean
  currency: 'USD' | 'EUR'
}

export interface UserState {
  allowedCountries: Country[]
  apiCredentialList?: any[]
  companyConsumption?: TimeStampValue<CompanyConsumption>
  companyKpi?: TimeStampValue<Kpi[]>
  invoiceInfo?: TimeStampValue<InvoiceInfo>
  jobList?: TimeStampValue<JobDetail[]>
  jobListSortDirection: typeof SORT_ASCENDING | typeof SORT_DESCENDING
  lastJobId?: string
  lastWebhookLogId?: string
  servicesInfo?: TimeStampValue<ServiceInfo>
  userInfo?: TimeStampValue<UserInfo>
  webhooks: Webhook[]
  webhookLogList: WebhookLog[]
  webhookLogListSortDirection: typeof SORT_ASCENDING | typeof SORT_DESCENDING
}

export const initialState: UserState = {
  allowedCountries: [],
  apiCredentialList: undefined,
  companyConsumption: undefined,
  companyKpi: undefined,
  invoiceInfo: undefined,
  jobList: undefined,
  jobListSortDirection: SORT_DESCENDING,
  lastJobId: undefined,
  lastWebhookLogId: undefined,
  servicesInfo: undefined,
  userInfo: undefined,
  webhooks: [],
  webhookLogList: [],
  webhookLogListSortDirection: SORT_DESCENDING,
}

export const UserSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetUserSlice: () => initialState,
    setAllowedCountries: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.allowedCountries>
    ) => {
      state.allowedCountries = action.payload
    },
    setApiCredentialList: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.apiCredentialList>
    ) => {
      state.apiCredentialList = action.payload
    },
    setCompanyConsumption: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.companyConsumption>
    ) => {
      state.companyConsumption = action.payload
    },
    setCompanyKpi: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.companyKpi>
    ) => {
      state.companyKpi = action.payload
    },
    setInvoiceInfo: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.invoiceInfo>
    ) => {
      state.invoiceInfo = action.payload
    },
    setJobList: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.jobList>
    ) => {
      state.jobList = action.payload
    },
    setJobListSortDirection: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.jobListSortDirection>
    ) => {
      state.jobListSortDirection = action.payload
    },
    setLastJobId: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.lastJobId>
    ) => {
      state.lastJobId = action.payload
    },
    setLastWebhookLogId: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.lastWebhookLogId>
    ) => {
      state.lastWebhookLogId = action.payload
    },
    setServicesInfo: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.servicesInfo>
    ) => {
      state.servicesInfo = action.payload
    },
    setUserInfo: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.userInfo>
    ) => {
      state.userInfo = action.payload
    },
    setWebhooks: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.webhooks>
    ) => {
      state.webhooks = action.payload
    },
    setWebhookLogs: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.webhookLogList>
    ) => {
      state.webhookLogList = action.payload
    },
    setWebhookLogsSortDirection: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.webhookLogListSortDirection>
    ) => {
      state.webhookLogListSortDirection = action.payload
    },
  },
})

export const getUserState = (state: { user: UserState }) => state.user

export const {
  resetUserSlice,
  setAllowedCountries,
  setApiCredentialList,
  setCompanyConsumption,
  setCompanyKpi,
  setInvoiceInfo,
  setJobList,
  setJobListSortDirection,
  setLastJobId,
  setLastWebhookLogId,
  setServicesInfo,
  setUserInfo,
  setWebhooks,
  setWebhookLogs,
  setWebhookLogsSortDirection,
} = UserSlice.actions

export default UserSlice.reducer
